import React, { FC, ReactElement } from 'react';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import FilterCheckbox from 'buyside/components/presentation/Filters/FilterCheckbox/filterCheckbox';
import FilterChecklist from 'buyside/components/presentation/Filters/FilterChecklist/filterChecklist';
import FilterDatePicker from 'buyside/components/presentation/Filters/FilterDatePicker/filterDatePicker';
import FilterSliderRange from 'buyside/components/presentation/Filters/FilterSliderRange/FilterSliderRange';
import FilterSelect from 'buyside/components/presentation/Filters/FilterSelect/filterSelect';
import FilterSliderSingle from 'buyside/components/presentation/Filters/FilterSliderSingle/filterSliderSingle';
import FilterTextInput from 'buyside/components/presentation/Filters/FilterTextInput/filterTextInput';
import IdeaIndexSavedSearchContainer from 'buyside/components/container/IdeaIndex/IdeaIndexSavedSearchContainer';
import { IdeaIndexJoyrideTarget } from 'buyside/components/presentation/Joyride/IdeaIndexJoyride/ideaIndexJoyrideHelper';
import SelectableDropdown from 'buyside/components/presentation/SelectableDropdown/selectableDropdown';

import {
  directionList,
  sliders,
  fundamentals,
  fundamentalNames,
  multiplesRatios,
  minExpectedReturn,
  performanceLabels,
  userRatingsCount,
  communityRatings,
  communityRatingIncrements,
  timeframes,
  portfolioPercentages,
  seniority,
  sortOrders
} from 'buyside/util/ideaFilterConstants';
import { newIdeaUrl } from 'buyside/util/urlHelper';
import { generateRangeMapping } from 'buyside/util/filterSliderUtil/filterSliderUtil';
import { labelizedFilterListOptions } from 'buyside/components/presentation/Filters/filterUtil';
import formatNumber from 'buyside/util/formatNumber';

const {
  assetClasses,
  catalysts,
  countries,
  geographies,
  ideaAttachmentChannelChecks,
  ideaAttachmentFinancialModels,
  ideaAttachmentSecondaryResearch,
  investmentSituations,
  rankingCategories,
  sectors
} = labelizedFilterListOptions;

import styles from './ideaIndexPageFilterStyles.scss';

const {
  standard_credit_rating: { increments: scrIncrements, ticks: scrTicks },
  moody_credit_rating: { increments: mcrIncrements, ticks: mcrTicks },
  fitch_credit_rating: { increments: fcrIncrements, ticks: fcrTicks },
  amount_outstanding: { increments: aoIncrements, ticks: aoTicks }
} = sliders;

export const renderIdeaCheckboxes = (isPublic: boolean): ReactElement => (
  <>
    {isPublic ? null : (
      <>
        <FilterCheckbox text="Only show my watchlisted tickers" paramName="watchlist_tickers" />
        <FilterCheckbox text="Only show Open Access ideas" paramName="open_access_ideas" />
      </>
    )}
    <FilterCheckbox
      text="Exclude submissions by non-buyside members"
      paramName="exclude_non_buyside"
    />
  </>
);

export const renderIdeaSideButtons = (isPublic: boolean, canWriteIdeas: boolean): ReactElement => (
  <>
    {isPublic ? null : (
      <>
        <SelectableDropdown
          title="Saved Searches"
          analyticsTag="saved-searches"
          color="green"
          joyrideTarget={IdeaIndexJoyrideTarget.SAVED_SEARCHES}
        >
          <div>
            <IdeaIndexSavedSearchContainer />
          </div>
        </SelectableDropdown>
        {canWriteIdeas && (
          <Button
            text="Submit Research"
            type={BUTTON_TYPES.MAJOR}
            size={BUTTON_SIZES.SMALL}
            url={newIdeaUrl()}
            joyrideTarget={IdeaIndexJoyrideTarget.SUBMIT_RESEARCH}
          />
        )}
      </>
    )}
  </>
);

export const renderIdeaSortOrders = (): ReactElement => (
  <FilterSelect paramName="sort_by" options={sortOrders} borderless />
);

export const HomeSavedSearchButton = (): ReactElement => (
  <SelectableDropdown title="Saved Searches" color="green" isDarkTheme>
    <IdeaIndexSavedSearchContainer />
  </SelectableDropdown>
);

const IdeaIndexPageFilter: FC<{
  allowPremiumFilters: boolean;
  isHome?: boolean;
}> = ({ allowPremiumFilters, isHome }): ReactElement => {
  return (
    <>
      <FilterTextInput
        id="ticker-search"
        placeholder="Ticker Search"
        analyticsTag="ticker-search"
        disabled={!allowPremiumFilters}
        paramName="ticker"
        isDarkTheme={isHome}
      />
      <FilterTextInput
        id="keyword-search"
        placeholder="Keyword Search"
        paramName="text"
        analyticsTag="keyword-search"
        disabled={!allowPremiumFilters}
        isDarkTheme={isHome}
      />
      <SelectableDropdown
        title="Direction"
        analyticsTag="direction"
        isDarkTheme={isHome}
        overflowVisible
      >
        <FilterSelect paramName="recommendation" options={directionList} placeholder="All" />
      </SelectableDropdown>
      <SelectableDropdown title="Market Cap" analyticsTag="market-cap" isDarkTheme={isHome}>
        <FilterSliderRange
          paramName="market_cap"
          mapping={generateRangeMapping(
            sliders.market_cap.increments,
            sliders.market_cap.ticks,
            formatNumber
          )}
          increments={sliders.market_cap.increments}
          handleTipFormatter={formatNumber}
          className={styles.marketCap}
        />
      </SelectableDropdown>
      <SelectableDropdown
        title="Geography"
        analyticsTag="geography"
        isDarkTheme={isHome}
        overflowVisible
      >
        <FilterSelect text="Country" options={countries} paramName="country" />
        <FilterChecklist filterList={geographies} disabled={false} paramName="geographies[]" />
      </SelectableDropdown>
      <SelectableDropdown title="Sectors" analyticsTag="sectors" isDarkTheme={isHome}>
        <FilterChecklist filterList={sectors} disabled={false} paramName="sectors[]" />
      </SelectableDropdown>
      <SelectableDropdown title="Fundamentals" analyticsTag="fundamentals" isDarkTheme={isHome}>
        {fundamentals
          .filter(({ name }): boolean => name !== fundamentalNames.MARKET_CAP)
          .map(
            ({ name, label }): ReactElement => {
              const { increments, ticks } = sliders[name];

              return (
                <FilterSliderRange
                  className={styles.fundamentals}
                  key={name + label}
                  text={label}
                  paramName={name}
                  mapping={generateRangeMapping(increments, ticks, formatNumber)}
                  increments={increments}
                  handleTipFormatter={formatNumber}
                />
              );
            }
          )}
      </SelectableDropdown>
      <SelectableDropdown
        title="Date Submitted"
        analyticsTag="date-submitted"
        disabled={!allowPremiumFilters}
        isDarkTheme={isHome}
        overflowVisible
      >
        <FilterDatePicker text="Submitted After" paramName="opened_after" />
        <FilterDatePicker text="Submitted Before" paramName="opened_before" />
      </SelectableDropdown>
      <SelectableDropdown
        title="Rankings"
        analyticsTag="rankings-filter"
        disabled={!allowPremiumFilters}
        isDarkTheme={isHome}
      >
        <FilterChecklist paramName="ranking_categories[]" filterList={rankingCategories} />
      </SelectableDropdown>
      <SelectableDropdown
        title="Multiples/Ratios"
        analyticsTag="multiples-ratios"
        isDarkTheme={isHome}
      >
        {multiplesRatios.map(
          ({ name, label }): ReactElement => {
            const { increments, ticks } = sliders[name];

            return (
              <FilterSliderRange
                key={name + label}
                text={label}
                paramName={name}
                mapping={generateRangeMapping(increments, ticks)}
                increments={increments}
                className={styles.multiplesRatios}
              />
            );
          }
        )}
      </SelectableDropdown>
      <SelectableDropdown title="Catalyst Types" analyticsTag="catalyst-types" isDarkTheme={isHome}>
        <FilterChecklist paramName="catalysts[]" filterList={catalysts} />
      </SelectableDropdown>
      <SelectableDropdown
        title="Performance"
        analyticsTag="performance"
        disabled={!allowPremiumFilters}
        isDarkTheme={isHome}
      >
        <FilterSliderSingle
          paramName="expected_return"
          ticks={minExpectedReturn}
          interval={1}
          text="Min. Expected Return"
          className={styles.expectedReturn}
        />

        {performanceLabels.map(
          ({ name, label }): ReactElement => (
            <FilterSliderRange
              key={`performance-label-${label}`}
              mapping={generateRangeMapping(sliders.returns.increments, sliders.returns.ticks)}
              paramName={name}
              increments={sliders.returns.increments}
              text={label}
            />
          )
        )}
      </SelectableDropdown>
      <SelectableDropdown title="Situations" analyticsTag="situations" isDarkTheme={isHome}>
        <FilterChecklist
          paramName="situations[]"
          filterList={investmentSituations}
          disabled={false}
        />
      </SelectableDropdown>
      <SelectableDropdown
        title="Ratings"
        analyticsTag="community_ratings"
        disabled={!allowPremiumFilters}
        isDarkTheme={isHome}
        overflowVisible
      >
        <FilterSelect
          text="Number of Ratings"
          paramName="user_ratings_count"
          options={userRatingsCount}
        />
        <FilterSliderRange
          text="Community Rating"
          paramName="community_rating"
          increments={communityRatingIncrements}
          mapping={generateRangeMapping(communityRatingIncrements, communityRatings)}
        />
      </SelectableDropdown>
      <SelectableDropdown
        title="Position Details"
        analyticsTag="position-details"
        isDarkTheme={isHome}
        overflowVisible
      >
        <FilterSelect text="Expected Timeframe" paramName="timeframe" options={timeframes} />
        <FilterSelect
          text="Portfolio Percentage"
          paramName="portfolio_percentage"
          options={portfolioPercentages}
        />
      </SelectableDropdown>
      <SelectableDropdown
        title="Attachment Types"
        analyticsTag="attachment-types"
        isDarkTheme={isHome}
      >
        <FilterCheckbox text="Show Ideas With a Formatted Thesis" paramName="formatted_thesis" />
        <FilterChecklist
          filterList={ideaAttachmentFinancialModels}
          paramName="financial_model_types[]"
          title="Financial Model"
        />
        <FilterChecklist
          filterList={ideaAttachmentChannelChecks}
          paramName="channel_check_types[]"
          title="Channel Check"
        />
        <FilterChecklist
          filterList={ideaAttachmentSecondaryResearch}
          paramName="secondary_research_types[]"
          title="Secondary Research"
        />
      </SelectableDropdown>
      <SelectableDropdown title="Credit Ratings" analyticsTag="credit-ratings" isDarkTheme={isHome}>
        <FilterSliderRange
          text="S &amp; P Rating"
          mapping={generateRangeMapping(scrIncrements, scrTicks)}
          paramName="standard_credit_rating"
          increments={scrIncrements}
        />
        <FilterSliderRange
          text="Moody's Rating"
          mapping={generateRangeMapping(mcrIncrements, mcrTicks)}
          paramName="moody_credit_rating"
          increments={mcrIncrements}
        />
        <FilterSliderRange
          text="Fitch Rating"
          mapping={generateRangeMapping(fcrIncrements, fcrTicks)}
          paramName="fitch_credit_rating"
          increments={fcrIncrements}
        />
      </SelectableDropdown>
      <SelectableDropdown
        title="Maturity Date"
        analyticsTag="maturity-date"
        isDarkTheme={isHome}
        overflowVisible
      >
        <FilterDatePicker text="From" paramName="maturity_date_from" />
        <FilterDatePicker text="To" paramName="maturity_date_to" />
      </SelectableDropdown>
      <SelectableDropdown
        title="Debt Outstanding"
        analyticsTag="amount_outstanding"
        isDarkTheme={isHome}
      >
        <FilterSliderRange
          paramName="amount_outstanding"
          mapping={generateRangeMapping(aoIncrements, aoTicks, formatNumber)}
          increments={aoIncrements}
          handleTipFormatter={formatNumber}
        />
      </SelectableDropdown>
      <SelectableDropdown title="Seniority" analyticsTag="seniority" isDarkTheme={isHome}>
        <FilterChecklist filterList={seniority} paramName="seniority[]" disabled={false} />
      </SelectableDropdown>
      <SelectableDropdown title="Asset Classes" analyticsTag="asset-classes" isDarkTheme={isHome}>
        <FilterChecklist filterList={assetClasses} paramName="asset_classes[]" disabled={false} />
      </SelectableDropdown>
    </>
  );
};

export default IdeaIndexPageFilter;
